import { takeLatest, takeEvery } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';
import debug from 'debug';

import * as actions from 'actions/adReview';
import * as adsApi from 'api/ads';

import { UPDATE_AD_FILTERS } from 'actions/filterAds';
import { getWgToken } from 'selectors/auth';
import {
  getSearchWord,
  getStatusFilter,
  getPaging,
  getSortedBy,
} from 'selectors/ads';

import { DEFAULT_PARAMS } from 'constants/BrowseAds';
import { FILTER_TO_STATUS } from 'mappers/status';

const log = debug('sagas:adReview');

const {
  FETCH_ADS_REQUESTED,
  REFRESH_ADS_REQUESTED,
  REVIEW_AUDIO_ADS_REQUESTED,
  APPROVE_AD_SUBMITTED,
  REJECT_AD_SUBMITTED,
  CANCEL_AD_SUBMITTED,
  KICK_AD_SUBMITTED,
  REVISE_VO_SUBMITTED,
  REVISE_BG_SUBMITTED,
  FETCH_AD_REQUESTED,
} = actions;

export function* refreshAds(action) {
  const { params, wgToken } = action.payload;

  try {
    const ads = yield call(adsApi.refreshAds, params, wgToken);
    yield put(actions.refreshAdsSuccess(ads.items));
    yield put(actions.updatePagingInfo(ads.paging));
  } catch (e) {
    yield put(actions.refreshAdsFailed(e));
  }
}

export function* filterAds(action) {
  const wgToken = yield select(getWgToken);
  const searchText = yield select(getSearchWord);
  const statusFilter = yield select(getStatusFilter);
  const sortedBy = yield select(getSortedBy);
  const paging = yield select(getPaging);

  const {
    filter = statusFilter,
    activePage = DEFAULT_PARAMS.activePage,
    orderBy = sortedBy,
    order = DEFAULT_PARAMS.order,
    searchWord = searchText,
  } = action.payload;

  const status = FILTER_TO_STATUS[filter];
  const offset = (activePage - 1) * paging.limit;
  const params = {
    ...DEFAULT_PARAMS,
    status,
    offset,
    activePage,
    orderBy,
    order,
    searchWord,
  };

  try {
    yield put(actions.refreshAds(params, wgToken));
  } catch (e) {
    log(`filtering ads failed. Params: ${params.toString()}`);
    yield put(actions.refreshAdsFailed(e));
  }
}

export function* fetchReviewAudioAds(action) {
  const { params, wgToken } = action.payload;

  try {
    const ads = yield call(adsApi.refreshAds, params, wgToken);
    yield put(actions.reviewAudioAdsSuccess(ads.items));
    yield put(actions.updateReviewAudioPagingInfo(ads.paging));
  } catch (e) {
    yield put(actions.reviewAudioAdsFailed(e));
  }
}

export function* fetchAds(action) {
  const { offset, limit, wgToken } = action.payload;

  try {
    const ads = yield call(adsApi.getAds, offset, limit, wgToken);
    yield put(actions.fetchAdsSuccess(ads.items));
    yield put(actions.updatePagingInfo(ads.paging));
  } catch (e) {
    yield put(actions.fetchAdsFailed(e));
  }
}

export function* fetchAd(action) {
  const { id, wgToken } = action.payload;

  try {
    const ad = yield call(adsApi.getAd, id, wgToken);
    yield put(actions.fetchAdSuccess(ad.items[0]));
  } catch (e) {
    if (e instanceof adsApi.AdNotFoundError) {
      yield put(actions.fetchAdNotFound(e));
      return;
    }

    yield put(actions.fetchAdFailed(e));
  }
}

export function* approveAd(action) {
  const { id } = action;
  const wgToken = yield select(getWgToken);

  try {
    yield call(adsApi.approveAd, id, wgToken);
    yield put(actions.approveAdSuccess(id));
  } catch (e) {
    yield put(actions.approveAdFailed(e, id));
  }
}

export function* reviseVO(action) {
  const { reviseVOInstructions, id } = action;

  try {
    const wgToken = yield select(getWgToken);
    yield call(adsApi.reviseVO, id, reviseVOInstructions, wgToken);
    yield put(actions.reviseAdSuccess(id));
  } catch (e) {
    yield put(actions.reviseAdFailed(e, id));
  }
}

export function* reviseBGMusic(action) {
  const { bgAssetId, id } = action;

  try {
    const wgToken = yield select(getWgToken);
    yield call(adsApi.reviseBGMusic, id, bgAssetId, wgToken);
    yield put(actions.reviseAdSuccess(id));
  } catch (e) {
    yield put(actions.reviseAdFailed(e, id));
  }
}


export function* rejectAd(action) {
  const { reason, id } = action;

  if (!reason) {
    yield put(actions.rejectAdPrompted(id));
    return;
  }

  try {
    const wgToken = yield select(getWgToken);
    yield call(adsApi.rejectAd, id, reason, wgToken);
    yield put(actions.rejectAdSuccess(id));
  } catch (e) {
    yield put(actions.rejectAdFailed(e, id));
  }
}

export function* cancelAd(action) {
  const { id } = action;
  const wgToken = yield select(getWgToken);

  try {
    yield call(adsApi.cancelAd, id, wgToken);
    yield put(actions.cancelAdSuccess(id));
  } catch (e) {
    yield put(actions.cancelAdFailed(e, id));
  }
}

export function* kickAd(action) {
  const { id } = action;
  const wgToken = yield select(getWgToken);

  try {
    yield call(adsApi.kickAd, id, wgToken);
    yield put(actions.kickAdSuccess(id));
  } catch (e) {
    yield put(actions.kickAdFailed(e, id));
  }
}


function* watchFetchAd() {
  yield takeLatest(FETCH_AD_REQUESTED, fetchAd);
}

function* watchFetchAds() {
  yield takeEvery(FETCH_ADS_REQUESTED, fetchAds);
}

function* watchRefreshAds() {
  yield takeEvery(REFRESH_ADS_REQUESTED, refreshAds);
}

function* watchFilterAds() {
  yield takeEvery(UPDATE_AD_FILTERS, filterAds);
}

function* watchReviewAudioAds() {
  yield takeEvery(REVIEW_AUDIO_ADS_REQUESTED, fetchReviewAudioAds);
}

function* watchApproveAd() {
  yield takeEvery(APPROVE_AD_SUBMITTED, approveAd);
}

function* watchReviseVO() {
  yield takeEvery(REVISE_VO_SUBMITTED, reviseVO);
}

function* watchReviseBG() {
  yield takeEvery(REVISE_BG_SUBMITTED, reviseBGMusic);
}

function* watchRejectAd() {
  yield takeEvery(REJECT_AD_SUBMITTED, rejectAd);
}

function* watchCancelAd() {
  yield takeEvery(CANCEL_AD_SUBMITTED, cancelAd);
}

function* watchKickAd() {
  yield takeEvery(KICK_AD_SUBMITTED, kickAd);
}

export function* watchAdReviewActions() {
  yield [
    watchFetchAds(),
    watchRefreshAds(),
    watchFilterAds(),
    watchReviewAudioAds(),
    watchFetchAd(),
    watchApproveAd(),
    watchReviseVO(),
    watchReviseBG(),
    watchRejectAd(),
    watchCancelAd(),
    watchKickAd(),
  ];
}



// WEBPACK FOOTER //
// ./src/middleware/sagas/adReview.js